import React, { useState, useEffect } from 'react';
import { MacWindow } from './MacWindow';
import { useSaaSContext, UserProfileInfo } from '../../store/SaaSContext';
import { useToast } from '../../store/ToastContext';
import { 
  User, 
  Lock, 
  Mail, 
  ShieldCheck, 
  UserPlus, 
  LogIn, 
  LogOut, 
  Key, 
  CheckCircle2, 
  HardDrive, 
  Sparkles, 
  X, 
  Trash2, 
  UserCheck 
} from 'lucide-react';

interface LocalAuthModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const LOCAL_ACCOUNTS_KEY = 'vdp_local_user_accounts_v1';

export const LocalAuthModal: React.FC<LocalAuthModalProps> = ({ isOpen, onClose }) => {
  const { user, setUser } = useSaaSContext();
  const { toast } = useToast();

  const [mode, setMode] = useState<'signin' | 'register' | 'switch'>('signin');
  
  // Form fields
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'admin' | 'user'>('user');

  // Stored local accounts list
  const [localAccounts, setLocalAccounts] = useState<UserProfileInfo[]>([]);

  useEffect(() => {
    try {
      const saved = localStorage.getItem(LOCAL_ACCOUNTS_KEY);
      if (saved) {
        const parsed: UserProfileInfo[] = JSON.parse(saved);
        const sanitized = parsed.filter(acc => acc && acc.email && acc.uid);
        setLocalAccounts(sanitized);
        localStorage.setItem(LOCAL_ACCOUNTS_KEY, JSON.stringify(sanitized));
        if (sanitized.length === 0) {
          setMode('register');
        }
      } else {
        localStorage.setItem(LOCAL_ACCOUNTS_KEY, JSON.stringify([]));
        setLocalAccounts([]);
        setMode('register');
      }
    } catch (e) {
      console.error('Failed to load local accounts', e);
    }
  }, [isOpen]);

  const saveAccountsToDisk = (accounts: UserProfileInfo[]) => {
    setLocalAccounts(accounts);
    try {
      localStorage.setItem(LOCAL_ACCOUNTS_KEY, JSON.stringify(accounts));
    } catch (e) {
      console.error('Failed to save accounts to disk', e);
    }
  };

  const hashPassword = async (pwd: string): Promise<string> => {
    if (typeof window !== 'undefined' && window.crypto?.subtle) {
      try {
        const encoder = new TextEncoder();
        const data = encoder.encode(pwd + '_vdp_local_salt_2026');
        const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
        const hashArray = Array.from(new Uint8Array(hashBuffer));
        return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      } catch {
        // Fallback below
      }
    }
    let hash = 0;
    for (let i = 0; i < pwd.length; i++) {
      hash = ((hash << 5) - hash) + pwd.charCodeAt(i);
      hash |= 0;
    }
    return 'fallback_' + Math.abs(hash).toString(16);
  };

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    if (!cleanEmail) {
      toast.warning('Email Required', 'Please enter your email address to sign in.');
      return;
    }
    if (!password) {
      toast.warning('Password Required', 'Please enter your password to sign in.');
      return;
    }

    const existing = localAccounts.find(a => a.email.toLowerCase() === cleanEmail);
    if (!existing) {
      toast.error('Account Not Found', 'No local account exists with this email. Please register first.');
      return;
    }

    const inputHash = await hashPassword(password);
    if (existing.passwordHash && existing.passwordHash !== inputHash) {
      toast.error('Authentication Failed', 'Incorrect password for this account.');
      return;
    }

    // Auto-upgrade legacy account with passwordHash if previously unhashed
    let accountToSet = existing;
    if (!existing.passwordHash) {
      accountToSet = { ...existing, passwordHash: inputHash };
      const updated = localAccounts.map(a => a.uid === existing.uid ? accountToSet : a);
      saveAccountsToDisk(updated);
    }

    setUser(accountToSet);
    try {
      localStorage.setItem('vdp_current_user_session', JSON.stringify(accountToSet));
    } catch {}
    toast.success('Signed In Successfully', `Welcome back, ${existing.name}!`);
    onClose();
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    const cleanEmail = email.trim().toLowerCase();
    const cleanName = name.trim();

    if (!cleanName || !cleanEmail) {
      toast.warning('Missing Fields', 'Please enter both your name and email address.');
      return;
    }

    if (!password || password.length < 4) {
      toast.warning('Weak Password', 'Please enter a password with at least 4 characters.');
      return;
    }

    const pwdHash = await hashPassword(password);
    // Security: Only the first registered user or explicitly selected role is applied; no automatic substring escalation
    const initialRole: 'admin' | 'user' = localAccounts.length === 0 ? 'admin' : (role === 'admin' ? 'admin' : 'user');

    const existingIndex = localAccounts.findIndex(a => a.email.toLowerCase() === cleanEmail);
    const newAcc: UserProfileInfo = {
      uid: existingIndex >= 0 ? localAccounts[existingIndex].uid : `user_${Date.now().toString(36)}`,
      name: cleanName,
      email: cleanEmail,
      avatar: `https://api.dicebear.com/7.x/bottts/svg?seed=${cleanEmail}`,
      role: initialRole,
      passwordHash: pwdHash,
      createdAt: new Date().toISOString()
    };

    let updated: UserProfileInfo[];
    if (existingIndex >= 0) {
      updated = [...localAccounts];
      updated[existingIndex] = newAcc;
    } else {
      updated = [newAcc, ...localAccounts];
    }

    saveAccountsToDisk(updated);
    setUser(newAcc);
    try {
      localStorage.setItem('vdp_current_user_session', JSON.stringify(newAcc));
    } catch {}

    toast.success('Local Account Registered', `Signed in as ${cleanName} (${initialRole === 'admin' ? 'Local Admin' : 'Standard User'}).`);
    onClose();
  };

  const handleSelectAccount = (acc: UserProfileInfo) => {
    setUser(acc);
    try {
      localStorage.setItem('vdp_current_user_session', JSON.stringify(acc));
    } catch {}
    toast.success('Switched User Profile', `Now logged in as ${acc.name} (${acc.email}).`);
    onClose();
  };

  const handleDeleteAccount = (acc: UserProfileInfo, e: React.MouseEvent) => {
    e.stopPropagation();
    const updated = localAccounts.filter(a => a.uid !== acc.uid);
    saveAccountsToDisk(updated);

    if (user?.uid === acc.uid) {
      const nextUser = updated.length > 0 ? updated[0] : null;
      setUser(nextUser);
      try {
        if (nextUser) {
          localStorage.setItem('vdp_current_user_session', JSON.stringify(nextUser));
        } else {
          localStorage.removeItem('vdp_current_user_session');
        }
      } catch {}
    }
    toast.info('Account Removed', `Deleted local user record for ${acc.email}.`);
  };

  const handleLogout = () => {
    setUser(null);
    try {
      localStorage.removeItem('vdp_current_user_session');
    } catch {}
    toast.info('Logged Out', 'Signed out of local session. Please log in to access user data.');
  };

  if (!isOpen) return null;

  return (
    <MacWindow
      isOpen={isOpen}
      onClose={onClose}
      title="Local Authentication & Application Database"
      maxWidth="max-w-lg"
    >
      <div className="flex flex-col bg-zinc-950 text-zinc-100 p-6 gap-6">
        
        {/* Top Header Card */}
        <div className="p-4 bg-zinc-900 border border-zinc-800 rounded-xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-600/20 border border-blue-500/30 flex items-center justify-center text-blue-400">
              <HardDrive className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-zinc-100 flex items-center gap-2">
                <span>Local App Authentication</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full font-mono bg-emerald-950 text-emerald-400 border border-emerald-800 font-bold">
                  Private DB
                </span>
              </h2>
              <p className="text-[11px] text-zinc-400 mt-0.5">
                {user ? `Currently logged in as ${user.name}` : 'Login or create a local account to access your stickers database.'}
              </p>
            </div>
          </div>

          {user && (
            <button
              onClick={handleLogout}
              className="px-3 py-1.5 bg-rose-950/80 hover:bg-rose-900 text-rose-300 border border-rose-800 rounded-lg text-xs font-bold transition-all cursor-pointer flex items-center gap-1.5"
            >
              <LogOut className="w-3.5 h-3.5" />
              Sign Out
            </button>
          )}
        </div>

        {/* Tab Switcher */}
        <div className="grid grid-cols-3 gap-1 p-1 bg-zinc-900 border border-zinc-800 rounded-xl text-xs font-bold">
          <button
            type="button"
            onClick={() => setMode('signin')}
            className={`py-2 rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
              mode === 'signin' ? 'bg-blue-600 text-white shadow-xs' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <LogIn className="w-3.5 h-3.5" />
            Sign In
          </button>

          <button
            type="button"
            onClick={() => setMode('register')}
            className={`py-2 rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
              mode === 'register' ? 'bg-blue-600 text-white shadow-xs' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <UserPlus className="w-3.5 h-3.5" />
            New Account
          </button>

          <button
            type="button"
            onClick={() => setMode('switch')}
            className={`py-2 rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 ${
              mode === 'switch' ? 'bg-blue-600 text-white shadow-xs' : 'text-zinc-400 hover:text-white'
            }`}
          >
            <UserCheck className="w-3.5 h-3.5" />
            Accounts ({localAccounts.length})
          </button>
        </div>

        {/* Mode 1: Sign In Form */}
        {mode === 'signin' && (
          <form onSubmit={handleSignIn} className="flex flex-col gap-4">
            <div>
              <label className="block text-xs font-bold text-zinc-300 mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="email"
                  required
                  placeholder="e.g. you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-zinc-900 border border-zinc-700 text-white rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-600 font-mono"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-zinc-300 mb-1.5">
                Password / Passcode
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="password"
                  placeholder="Local password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-zinc-900 border border-zinc-700 text-white rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-600 font-mono"
                />
              </div>
            </div>

            <div className="flex items-center justify-end pt-2">
              <button
                type="submit"
                className="w-full sm:w-auto px-6 py-2.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer shadow-md flex items-center justify-center gap-1.5"
              >
                <LogIn className="w-4 h-4" />
                Sign In To Local DB
              </button>
            </div>
          </form>
        )}

        {/* Mode 2: Register New Account Form */}
        {mode === 'register' && (
          <form onSubmit={handleRegister} className="flex flex-col gap-4">
            <div>
              <label className="block text-xs font-bold text-zinc-300 mb-1.5">
                Full Name
              </label>
              <div className="relative">
                <User className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="text"
                  required
                  placeholder="e.g. Rahul Sharma"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-zinc-900 border border-zinc-700 text-white rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-600"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-zinc-300 mb-1.5">
                Email Address
              </label>
              <div className="relative">
                <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="email"
                  required
                  placeholder="e.g. rahul@printshop.local"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-zinc-900 border border-zinc-700 text-white rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-600 font-mono"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-zinc-300 mb-1.5">
                Password
              </label>
              <div className="relative">
                <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  type="password"
                  placeholder="Enter passcode"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-zinc-900 border border-zinc-700 text-white rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-600 font-mono"
                />
              </div>
            </div>

            <button
              type="submit"
              className="mt-2 w-full py-2.5 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-lg transition-colors cursor-pointer shadow-md flex items-center justify-center gap-1.5"
            >
              <UserPlus className="w-4 h-4" />
              Register Account &amp; Access Database
            </button>
          </form>
        )}

        {/* Mode 3: Switch Accounts List */}
        {mode === 'switch' && (
          <div className="flex flex-col gap-3">
            <div className="text-xs font-bold text-zinc-400 uppercase tracking-wider">
              Saved Local User Profiles
            </div>

            <div className="max-h-60 overflow-y-auto flex flex-col gap-2 pr-1">
              {localAccounts.map((acc) => {
                const isActive = user?.uid === acc.uid;
                return (
                  <div
                    key={acc.uid}
                    onClick={() => handleSelectAccount(acc)}
                    className={`p-3 rounded-xl border flex items-center justify-between gap-3 cursor-pointer transition-all ${
                      isActive
                        ? 'bg-blue-950/60 border-blue-500 text-white ring-1 ring-blue-500/50'
                        : 'bg-zinc-900 border-zinc-800 hover:bg-zinc-850 hover:border-zinc-700 text-zinc-300'
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <img src={acc.avatar} alt={acc.name} className="w-8 h-8 rounded-full border border-zinc-700 shrink-0" />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-bold text-xs truncate">{acc.name}</span>
                          {acc.role === 'admin' && (
                            <span className="text-[9px] px-1.5 py-0.2 rounded font-mono font-bold bg-amber-950 text-amber-300 border border-amber-800">
                              Admin
                            </span>
                          )}
                          {isActive && (
                            <span className="text-[9px] px-1.5 py-0.2 rounded font-mono font-bold bg-blue-900 text-blue-300 border border-blue-700">
                              Active Session
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-zinc-500 font-mono truncate">{acc.email}</div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={(e) => handleDeleteAccount(acc, e)}
                      className="p-1.5 text-zinc-500 hover:text-rose-400 hover:bg-zinc-800 rounded-lg transition-colors cursor-pointer"
                      title="Delete local account"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </MacWindow>
  );
};
